// React context shared by every field component, plus the renderer's public
// prop and field-component contracts. Field components read mutators and shared
// state from here rather than via prop drilling; the renderer is the sole
// provider.

import { createContext, useContext, type ReactElement } from 'react'
import type {
  DataSourceDescriptor,
  DynamicFormField,
  Option,
  UploadAdapter,
  ValidationError,
  ValidationErrors,
} from './types'

/**
 * Resolve a field's async option set from the raw Alpaca data-source descriptor
 * the importer stored in `config.data_source_raw`. Hosts supply this; without
 * it, data-source-backed enums render with no options.
 */
export type DataSourceResolver = (
  descriptor: DataSourceDescriptor,
  field: DynamicFormField,
) => Promise<Option[]>

export type DynamicFormMode = 'edit' | 'readonly' | 'preview'

/**
 * Host-supplied class-name overrides. Defaults are Bootstrap 5 classes; a host
 * without Bootstrap can swap in its own utility classes. Only the slots needed
 * so far are defined; later field commits extend this.
 */
export interface ClassNameOverrides {
  fieldContainer?: string
  label?: string
  requiredIndicator?: string
  control?: string
  select?: string
  checkInput?: string
  /** Wrapper around a checkbox's nested `<label><input><span>` (default `checkbox`). */
  checkContainer?: string
  /** Wrapper around a radio option's nested `<label><input><span>` (default `radio`).
   *  Separate from `checkContainer` because the app's font-weight reset keys off
   *  the `.checkbox` / `.radio` container class. */
  radioContainer?: string
  /** The `<label>` that wraps a checkbox or radio input + its text span
   *  (default `label--flex`). */
  checkLabel?: string
  description?: string
  helpText?: string
  invalidFeedback?: string
  group?: string
  /** Unlabeled (wrapper) groups; defaults to `mb-3`. */
  groupUnlabeled?: string
  groupLegend?: string
  /** OPT-IN: when set, group `description` renders under the legend with this
   *  class. Left unset (the default), descriptions don't render — preserving
   *  the historical group output. */
  groupDescription?: string
  /** Utilities carried alongside the `config.style` classes on a styled group;
   *  defaults to `mb-3`. Styled groups replace `group`/`groupUnlabeled`, so this
   *  is the only spacing hook they have. */
  groupStyled?: string
  /** Legend of a styled group; defaults to `fs-6 fw-semibold`. Separate from
   *  `groupLegend` because a host's section-card header styling looks wrong
   *  inside an alert. */
  groupStyledLegend?: string
  /** Enhanced (select2-style) multi-select slots, consumed by the adapter. */
  enhancedSelectContainer?: string
  enhancedSelectChip?: string
  enhancedSelectChipRemove?: string
  enhancedSelectSearchInput?: string
  enhancedSelectMenu?: string
  enhancedSelectOption?: string
  enhancedSelectOptionHighlighted?: string
}

/** Props every field component receives from the dispatcher. */
export interface FieldComponentProps {
  field: DynamicFormField
  /** Full value path, e.g. "address.zip" or "contacts[0].email". */
  path: string
  value: unknown
  errors: ValidationError[]
}

export type FieldComponent = (props: FieldComponentProps) => ReactElement | null

/** Per-type component overrides a host can supply via the `fieldComponents` prop. */
export type FieldComponentOverrides = Partial<Record<string, FieldComponent>>

/**
 * A renderer for a `markup`-typed field carrying a `config.widget` name (the
 * importer maps rare Alpaca widgets — country, state, image, chem_doodle,
 * chem_writer — to `markup` + `config.widget`). Same props shape as a field
 * component; hosts register these by widget name via `widgetComponents`.
 */
export type WidgetComponent = FieldComponent

/** Widget renderers keyed by `config.widget` name, supplied via `widgetComponents`. */
export type WidgetComponentOverrides = Partial<Record<string, WidgetComponent>>

/**
 * Props the renderer hands a {@link RichTextAdapter}. Mirrors the attributes a
 * plain `<textarea>` would receive (id, disabled, placeholder, aria-*) so the
 * editor keeps the same accessibility and labelling contract.
 */
export interface RichTextEditorProps {
  id: string
  /** Current value as an HTML string. */
  value: string
  disabled?: boolean
  placeholder?: string
  ariaInvalid?: true | undefined
  ariaRequired?: true | undefined
  ariaDescribedBy?: string
  /** Id of the element naming the editor. A `<label for>` names native controls
   *  only, and an editor is usually a contentEditable `div`. */
  ariaLabelledBy?: string
  /** Emit the edited HTML. The adapter MUST emit sanitized HTML (see richText.ts). */
  onChange(html: string): void
  onBlur?(): void
}

/**
 * Host-supplied rich-text editor component. The core package bundles no editor;
 * a host injects one via the `richTextAdapter` prop, so the editor library can
 * be swapped by changing a single module (parallels {@link UploadAdapter}).
 */
export type RichTextAdapter = (props: RichTextEditorProps) => ReactElement

/**
 * Props the renderer hands an {@link EnhancedMultiSelectAdapter}. The field
 * component stays the source of truth: options arrive already resolved and
 * visibility-filtered with their JSON-native values, and the adapter reports
 * the next selected-values array back verbatim (never stringified).
 */
export interface EnhancedMultiSelectProps {
  /** Goes on the combobox input — the label `htmlFor` / `focusField` target. */
  id: string
  /** Already `visibleOptions`-filtered; values keep their JSON type. */
  options: Option[]
  /** Currently selected values (JSON-typed). */
  selected: unknown[]
  disabled?: boolean
  placeholder?: string
  /** Append `is-invalid` styling when true. */
  hasErrors: boolean
  ariaInvalid?: true | undefined
  ariaRequired?: true | undefined
  ariaDescribedBy?: string
  /** Block adding past this count (`config.max_items`); validation owns messaging. */
  maxItems?: number
  classNames: ClassNameOverrides
  onChange(next: unknown[]): void
  onBlur?(): void
}

/**
 * Host-supplied select2-style multi-select control. The core package bundles
 * none; a host injects one via the `enhancedSelectAdapter` prop (the reference
 * downshift-backed adapter ships as the `./enhanced-select` entry, parallel to
 * `./richtext-simple`). When absent — or when a field sets
 * `config.plain_select` — `multi_select` falls back to the native
 * `<select multiple>`.
 */
export type EnhancedMultiSelectAdapter = (props: EnhancedMultiSelectProps) => ReactElement

/**
 * Props the renderer hands an {@link EnhancedSingleSelectAdapter}. Mirrors
 * {@link EnhancedMultiSelectProps} but for `select` fields: one JSON-typed
 * value in, one (or undefined, via the "none" choice) reported back.
 */
export interface EnhancedSingleSelectProps {
  /** Goes on the toggle control — the label `htmlFor` / `focusField` target. */
  id: string
  /** Already `visibleOptions`-filtered; values keep their JSON type. */
  options: Option[]
  /** Currently selected value (JSON-typed), or undefined when unset. */
  value: unknown
  disabled?: boolean
  /** Label for the clearable "none" choice; undefined hides it (`hide_none`). */
  noneLabel?: string
  hasErrors: boolean
  ariaInvalid?: true | undefined
  ariaRequired?: true | undefined
  ariaDescribedBy?: string
  classNames: ClassNameOverrides
  onChange(next: unknown): void
  onBlur?(): void
}

/**
 * Host-supplied custom dropdown for single `select` fields. The core package
 * bundles none; a host injects one via the `enhancedSingleSelectAdapter` prop
 * (the reference downshift-backed adapter ships from the `./enhanced-select`
 * entry). When absent — or when a field sets `config.plain_select` — `select`
 * falls back to the native `<select>`.
 */
export type EnhancedSingleSelectAdapter = (props: EnhancedSingleSelectProps) => ReactElement

/** Optional per-field renderer for content placed after the field label +
 *  required indicator (e.g. a "Double-check" pill). Return null to skip. */
export type LabelSuffixRenderer = (path: string) => ReactElement | null

export interface FieldContextValue {
  mode: DynamicFormMode
  classNames: ClassNameOverrides
  errors: ValidationErrors
  /** Optional label-suffix renderer; absent → no suffix on any label. */
  labelSuffix?: LabelSuffixRenderer
  /** Per-type component overrides supplied by the host. */
  fieldComponents: FieldComponentOverrides
  /** Widget renderers (by `config.widget` name) for passthrough markup widgets. */
  widgetComponents: WidgetComponentOverrides
  /** Resolves async option sets for data-source-backed enums (optional). */
  dataSourceResolver?: DataSourceResolver
  /** Persists files for `upload` fields (optional; without it uploads are disabled). */
  uploadAdapter?: UploadAdapter
  /**
   * Form-level flag (mirrors alpaca's `enable_rich_text`): upgrades ALL
   * `textarea` fields to rich editors. Individual fields can opt in instead via
   * `config.rich_text`. Without a {@link richTextAdapter} neither has any effect.
   */
  enableRichText?: boolean
  /**
   * Rich-text editor component used when {@link enableRichText} or a field's
   * `config.rich_text` is set (optional).
   */
  richTextAdapter?: RichTextAdapter
  /**
   * Select2-style multi-select control used by `multi_select` fields when
   * present (optional; fields fall back to the native `<select multiple>`).
   */
  enhancedSelectAdapter?: EnhancedMultiSelectAdapter
  /**
   * Custom dropdown used by `select` fields when present (optional; fields
   * fall back to the native `<select>`).
   */
  enhancedSingleSelectAdapter?: EnhancedSingleSelectAdapter
  /**
   * When true, field components render their label visually hidden (still
   * associated for accessibility). Set for `layout: "table"` repeatable groups
   * where the column header supplies the visible label.
   */
  hideFieldLabels?: boolean
  /** Write a single field value into the store (no validation). */
  setFieldValue(path: string, value: unknown): void
  /** Signal a field lost focus → blur-triggered validation of that field. */
  notifyBlur(path: string): void
}

export const FieldContext = createContext<FieldContextValue | null>(null)

export function useFieldContext(): FieldContextValue {
  const ctx = useContext(FieldContext)
  if (ctx == null) {
    throw new Error('Dynamic form field rendered outside of <DynamicFormRenderer>')
  }
  return ctx
}

/** True when controls should be non-interactive (readonly mode or field flags). */
export function isFieldDisabled(field: DynamicFormField, mode: DynamicFormMode): boolean {
  return mode === 'readonly' || field.disabled === true || field.readonly === true
}
